import { memo, useMemo } from "react";
import type {
  Category,
  ColumnId,
  Dice,
  Sheet,
  PotentialScores,
} from "@yamber/rules-engine";
import { getPotentialScores } from "@yamber/rules-engine";

interface ScoreSheetProps {
  sheet: Sheet;
  dice: Dice;
  rollCount: number;
  announcedCategory: Category | null;
  onScoreCell: (columnId: ColumnId, category: Category) => void;
}

const CATEGORY_LABELS: Record<Category, string> = {
  ones: "Ones",
  twos: "Twos",
  threes: "Threes",
  fours: "Fours",
  fives: "Fives",
  sixes: "Sixes",
  max: "Max",
  min: "Min",
  straight: "Straight",
  fullHouse: "Full House",
  poker: "Poker",
  yamb: "Yamb",
};

const CATEGORY_ORDER: Category[] = [
  "ones",
  "twos",
  "threes",
  "fours",
  "fives",
  "sixes",
  "max",
  "min",
  "straight",
  "fullHouse",
  "poker",
  "yamb",
];

const COLUMN_LABELS: Record<string, string> = {
  down: "Down",
  up: "Up",
  free: "Free",
  announce: "Announce",
};

export const ScoreSheet = memo(function ScoreSheet({
  sheet,
  dice,
  rollCount,
  announcedCategory,
  onScoreCell,
}: ScoreSheetProps) {
  const columnIds = useMemo(() => Object.keys(sheet.columns), [sheet.columns]);
  const canScore = rollCount > 0;

  // Memoize expensive potential score calculation
  const potential: PotentialScores | null = useMemo(
    () => (canScore ? getPotentialScores(dice, sheet, announcedCategory, rollCount) : null),
    [canScore, dice, sheet, announcedCategory, rollCount]
  );

  const availableCellSet = useMemo(
    () =>
      new Set(
        potential?.availableCells.map((c) => `${c.columnId}:${c.category}`) ?? []
      ),
    [potential]
  );

  const isAvailable = (columnId: string, category: Category) =>
    availableCellSet.has(`${columnId}:${category}`);

  return (
    <div className="score-sheet" role="region" aria-label="Score Sheet">
      <table className="score-sheet__table" aria-describedby="score-instructions">
        <caption id="score-instructions" className="sr-only">
          Select available cells to score your dice. Available cells are highlighted.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="score-sheet__header score-sheet__header--category">
              Category
            </th>
            {columnIds.map((colId) => (
              <th scope="col" key={colId} className="score-sheet__header">
                {COLUMN_LABELS[colId] ?? colId}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CATEGORY_ORDER.map((category) => (
            <tr key={category} className="score-sheet__row">
              <th scope="row" className="score-sheet__category">
                {CATEGORY_LABELS[category]}
              </th>
              {columnIds.map((colId) => {
                const cell = sheet.columns[colId]?.cells[category];
                const isFilled = cell?.score !== null;
                const available = isAvailable(colId, category);
                const potentialScore = potential?.scores[category] ?? 0;
                const columnLabel = COLUMN_LABELS[colId] ?? colId;
                const categoryLabel = CATEGORY_LABELS[category];

                return (
                  <td
                    key={colId}
                    className={`score-sheet__cell ${
                      isFilled ? "score-sheet__cell--filled" : ""
                    } ${available ? "score-sheet__cell--available" : ""}`}
                  >
                    {isFilled ? (
                      <span
                        className="score-sheet__score"
                        aria-label={`${categoryLabel} ${columnLabel}: ${cell?.score} points`}
                      >
                        {cell?.score}
                      </span>
                    ) : available ? (
                      <button
                        type="button"
                        className="score-sheet__score-btn"
                        onClick={() => onScoreCell(colId, category)}
                        aria-label={`Score ${potentialScore} for ${categoryLabel} in ${columnLabel} column`}
                      >
                        {potentialScore}
                      </button>
                    ) : (
                      <span className="score-sheet__empty" aria-label="Empty">-</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="score-sheet__totals">
            <th scope="row" className="score-sheet__category">Total</th>
            <td
              colSpan={columnIds.length}
              className="score-sheet__total-value"
              aria-live="polite"
            >
              {sheet.total}
              {sheet.upperBonus > 0 && (
                <span className="score-sheet__bonus">
                  {" "}
                  (includes +{sheet.upperBonus} bonus)
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
});
